import { getProgressText } from '@ui-common/utils'
import { ButtonProps } from '@ui-components/actions/Button'
import { InfoIcon } from '@ui-components/assets/Icons'
import { NotificationProperties } from '@ui-components/containers/Notification'
import { Colors } from '@ui-components/global/colors'
import { ProgressBarStepProps } from '@ui-components/global/ComponentProps'
import { IntroSectionProps } from '@ui-components/layout/IntroSection'
import {
  ConfirmButtonProps,
  WizardHeaderButtonProps,
  WizardPageSubHeaderProps,
} from '@ui-components/layout/WizardTypes'
import { SecondaryTopBarMenuOption } from '@ui-components/navigation/SecondaryTopBar'
import { ReactNode } from 'react'
import { IntlShape } from 'react-intl'

export enum BusyMode {
  Loading = 'loading',
  Saving = 'saving',
}

export interface WizardStepConfig {
  subHeaderOptions: WizardPageSubHeaderProps | undefined
  headerButtonProps: WizardHeaderButtonProps
  confirmButtonProps?: ConfirmButtonProps
  progressBarOptions?: ProgressBarStepProps
  pageIntro?: IntroSectionProps
  showIntroSectionDivider?: boolean
}

export interface StepActionHandlers {
  handleContinueClick: (() => void) | null
  handleBackClick: (() => void) | undefined
  handleInfoClick: (() => void) | undefined
  handleAbortClick: (event: React.BaseSyntheticEvent) => void
}

export const getProgressBarProps = ({
  intl,
  progressInfo,
  showProgressBar = true,
}: Pick<StepConfigurationParams, 'intl' | 'progressInfo' | 'showProgressBar'>): ProgressBarStepProps | undefined => {
  if (progressInfo && showProgressBar) {
    const { stepIndex, totalSteps } = progressInfo
    return {
      currentStepIndex: stepIndex,
      totalSteps,
      'aria-label': getProgressText(intl, {
        currentStepIndex: progressInfo.stepIndex,
        totalSteps: progressInfo.totalSteps,
      }),
    }
  }

  return undefined
}

export const getIntroSectionProps = ({
  stepTitle,
  leadText,
  additionalText,
  moreInfoButtonProps,
  notificationProps,
  progressText,
  showIntroSectionDivider,
}: Pick<
  StepConfigurationParams,
  | 'stepTitle'
  | 'leadText'
  | 'additionalText'
  | 'moreInfoButtonProps'
  | 'notificationProps'
  | 'progressText'
  | 'showIntroSectionDivider'
>): IntroSectionProps | undefined => {
  if (stepTitle) {
    return {
      title: stepTitle,
      leadText,
      additionalText,
      moreInfoButtonProps,
      notificationProps,
      progressText,
      showIntroSectionDivider,
    }
  }

  return undefined
}

const getSubHeaderProps = ({
  intl,
  stepTitle,
  isFixedSubHeader,
  secondaryNavigationDescription,
  progressBarOptions,
  secondaryTopBarMenuOptions,
  buttonHandlers,
  showProgressBar,
}: Pick<
  StepConfigurationParams,
  | 'intl'
  | 'stepTitle'
  | 'isFixedSubHeader'
  | 'secondaryNavigationDescription'
  | 'secondaryTopBarMenuOptions'
  | 'buttonHandlers'
  | 'showProgressBar'
> & { progressBarOptions: ProgressBarStepProps | undefined }): WizardPageSubHeaderProps | undefined => {
  const { handleBackClick, handleInfoClick } = buttonHandlers
  if (stepTitle) {
    return {
      title: stepTitle,
      description: secondaryNavigationDescription,
      isFixedSubHeader,
      showProgressBar,
      previousLinkButtonProps: handleBackClick
        ? {
            label: intl.formatMessage({ id: 'common-back' }),
            onClick: handleBackClick,
          }
        : undefined,
      buttonProps: handleInfoClick
        ? {
            label: intl.formatMessage({ id: 'common-info' }),
            onClick: handleInfoClick,
            icon: <InfoIcon style={{ color: Colors.ElementDefaultNeutral }} />,
          }
        : undefined,
      progressBarOptions,
      secondaryTopBarMenuProps: secondaryTopBarMenuOptions
        ? {
            menuOptions: secondaryTopBarMenuOptions,
            menuAriaLabelOpen: intl.formatMessage({ id: 'common-open-additional-features' }),
            menuAriaLabelClose: intl.formatMessage({ id: 'common-close-additional-features' }),
          }
        : undefined,
    }
  }

  return undefined
}

const getConfirmButtonProps = ({
  intl,
  isEditing,
  continueLabel,
  workInProgress,
  buttonHandlers,
  shouldDisableSubmit,
  shouldShowConfirmFloating,
}: Pick<
  StepConfigurationParams,
  | 'workInProgress'
  | 'continueLabel'
  | 'isEditing'
  | 'intl'
  | 'buttonHandlers'
  | 'shouldDisableSubmit'
  | 'shouldShowConfirmFloating'
>): ConfirmButtonProps | undefined => {
  const { handleContinueClick } = buttonHandlers
  let confirmLabel = continueLabel || intl.formatMessage({ id: 'common-continue' })

  if (workInProgress) {
    confirmLabel =
      workInProgress.busyMode === BusyMode.Loading
        ? intl.formatMessage({ id: 'common-loading' })
        : intl.formatMessage({ id: 'common-saving' })
  } else if (isEditing) {
    confirmLabel = intl.formatMessage({ id: 'common-save' })
  }

  return handleContinueClick
    ? {
        confirmLabel,
        onConfirm: handleContinueClick,
        disabled: shouldDisableSubmit,
        shouldShowConfirmFloating,
      }
    : undefined
}

type StepConfigurationParams = {
  stepTitle?: string
  intl: IntlShape
  isEditing: boolean
  buttonHandlers: StepActionHandlers
  continueLabel?: string
  progressInfo?: { stepIndex: number; totalSteps: number }
  workInProgress?: { busyMode: BusyMode }
  secondaryNavigationDescription?: string
  secondaryTopBarMenuOptions?: SecondaryTopBarMenuOption[]
  isFixedSubHeader?: boolean
  shouldDisableSubmit?: boolean
  shouldShowConfirmFloating?: boolean
  showProgressBar?: boolean
  leadText?: ReactNode
  additionalText?: ReactNode
  moreInfoButtonProps?: ButtonProps
  notificationProps?: NotificationProperties
  progressText?: string
  showIntroSectionDivider?: boolean
}

export const createStepConfigurations = (params: StepConfigurationParams): WizardStepConfig => {
  const {
    stepTitle,
    intl,
    isEditing,
    buttonHandlers,
    continueLabel,
    progressInfo,
    workInProgress,
    secondaryNavigationDescription,
    secondaryTopBarMenuOptions,
    isFixedSubHeader = false,
    shouldDisableSubmit = false,
    shouldShowConfirmFloating = false,
    showProgressBar = true,
    leadText,
    additionalText,
    moreInfoButtonProps,
    notificationProps,
    showIntroSectionDivider,
  } = params
  const { handleAbortClick } = buttonHandlers

  const progressBarOptions = getProgressBarProps({ intl, progressInfo, showProgressBar })
  const subHeaderOptions = getSubHeaderProps({
    intl,
    progressBarOptions,
    buttonHandlers,
    secondaryNavigationDescription,
    stepTitle,
    secondaryTopBarMenuOptions,
    isFixedSubHeader,
    showProgressBar,
  })
  const confirmButtonProps = getConfirmButtonProps({
    intl,
    isEditing,
    continueLabel,
    workInProgress,
    buttonHandlers,
    shouldDisableSubmit,
    shouldShowConfirmFloating,
  })
  const progressText = progressBarOptions && getProgressText(intl, progressBarOptions)
  const pageIntro = getIntroSectionProps({
    stepTitle,
    leadText,
    additionalText,
    moreInfoButtonProps,
    notificationProps,
    progressText,
    showIntroSectionDivider,
  })

  return {
    progressBarOptions,
    subHeaderOptions,
    confirmButtonProps,
    headerButtonProps: {
      headerButtonLabel: intl.formatMessage({ id: 'common-exit' }),
      onHeaderButtonClick: handleAbortClick,
    },
    pageIntro,
  }
}
